import React, { useId } from 'react';
import DashboardTheme from '../dashboard-theme';

interface Props {
  readonly value: number;
  readonly thresholdMin: number;
  readonly thresholdMax: number;
  readonly size?: number;
  readonly valueLabel?: string;
  /**
   * Renders the value text, needle and hub in the amber accent instead of navy, for the
   * dark-background kiosk/display screen. Navy-on-navy is invisible there, and amber matches the
   * accent that screen already uses for its header label and active view tab. Defaults to false so
   * every light-background dashboard tab keeps the approved mockup's navy styling.
   */
  readonly onDarkBackground?: boolean;
  /**
   * Scales the dial to the width of its container instead of rendering at a fixed pixel
   * <c>size</c>. Every coordinate below is derived from <c>size</c>, so with a viewBox that value
   * becomes a pure aspect-ratio unit and the same drawing renders crisply at any pixel size - a
   * 200px smartwatch or a 4K TV wall. Off by default: the dashboard tabs lay the dial out next to
   * fixed-width cards and want the exact pixel size they ask for.
   */
  readonly responsive?: boolean;
}

const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number): { x: number; y: number } => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
};

const arcPath = (cx: number, cy: number, r: number, angleStart: number, angleEnd: number): string => {
  const start = polarToCartesian(cx, cy, r, angleStart);
  const end = polarToCartesian(cx, cy, r, angleEnd);
  const largeArc = Math.abs(angleStart - angleEnd) > 180 ? 1 : 0;
  const sweep = angleStart > angleEnd ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
};

const valueToAngle = (v: number): number => 180 - (Math.min(Math.max(v, 0), 100) / 100) * 180;

// Semicircular capacity dial matching the approved dashboard-mockup.html gauge design exactly:
// gray-200 quiet zone, green-600 healthy zone, orange->red gradient over-capacity zone,
// navy-900 needle/text.
const Gauge = ({
  value,
  thresholdMin,
  thresholdMax,
  size = 150,
  valueLabel,
  onDarkBackground = false,
  responsive = false,
}: Props): React.JSX.Element => {
  const gradientId = useId();
  const foreground = onDarkBackground ? DashboardTheme.amber500 : DashboardTheme.navy900;
  const cx = size / 2;
  const cy = size / 2 - size * 0.035;
  const r = size / 2 - size * 0.13;
  const trackWidth = size * 0.082;

  const zoneQuiet = arcPath(cx, cy, r, 180, valueToAngle(thresholdMin));
  const zoneHealthy = arcPath(cx, cy, r, valueToAngle(thresholdMin), valueToAngle(thresholdMax));
  const zoneOver = arcPath(cx, cy, r, valueToAngle(thresholdMax), 0);

  const needleAngle = valueToAngle(value);
  const needleTip = polarToCartesian(cx, cy, r - trackWidth / 2 - 2, needleAngle);

  return (
    <svg
      width={responsive ? undefined : size}
      height={responsive ? undefined : size * 0.66}
      viewBox={`0 0 ${size} ${size * 0.66}`}
      preserveAspectRatio="xMidYMid meet"
      style={responsive ? { width: '100%', height: 'auto', display: 'block' } : undefined}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={DashboardTheme.orange500} />
          <stop offset="100%" stopColor={DashboardTheme.red500} />
        </linearGradient>
      </defs>
      <path d={zoneQuiet} stroke={DashboardTheme.gray200} strokeWidth={trackWidth} fill="none" strokeLinecap="round" />
      <path d={zoneHealthy} stroke={DashboardTheme.green600} strokeWidth={trackWidth} fill="none" strokeLinecap="round" />
      <path d={zoneOver} stroke={`url(#${gradientId})`} strokeWidth={trackWidth} fill="none" strokeLinecap="round" />
      <line
        x1={cx}
        y1={cy}
        x2={needleTip.x}
        y2={needleTip.y}
        stroke={foreground}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r={5} fill={foreground} />
      <text
        x={cx}
        y={cy - r * 0.28}
        textAnchor="middle"
        fontFamily={DashboardTheme.headerFontFamily}
        fontWeight={700}
        fontSize={size * 0.155}
        fill={foreground}
      >
        {valueLabel ?? `${value}%`}
      </text>
    </svg>
  );
};

export default Gauge;
